var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import difference from 'lodash/difference';
import filter from 'lodash/filter';
import findKey from 'lodash/findKey';
import first from 'lodash/first';
import forEach from 'lodash/forEach';
import forOwn from 'lodash/forOwn';
import last from 'lodash/last';
import sortBy from 'lodash/sortBy';
import throttle from 'lodash/throttle';
import { ADJACENT_SLICE_THRESHOLD, ADJACENT_SLICE_WEIGHT, CHILDREN_ENDPOINT_ACTION, CHILDREN_ENDPOINT_ACTION_RESULT, DEBUG_FN_COLORS, DEFAULT_KEY_MAP, DIAGONAL_SLICE_WEIGHT, DIRECTION_DOWN, DIRECTION_LEFT, DIRECTION_RIGHT, DIRECTION_UP, KEY_BACK, KEY_BACK_ANDROID_TV, KEY_BACK_TIZEN_TV, KEY_BACK_WEBOS_TV, KEY_ENTER, MAIN_COORDINATE_WEIGHT, THROTTLE_OPTIONS, } from './contants';
import { NavigationType, } from './types';
import checkChildrenEndpointAction from './utils/checkChildrenEndpointAction';
import getCutoffCoordinateFromCenter from './utils/getCutoffCoordinateFromCenter';
import getCutoffCoordinateFromEdge from './utils/getCutoffCoordinateFromEdge';
import getOppositeDirection from './utils/getOppositeDirection';
import getRefCornersFromCenter from './utils/getRefCornersFromCenter';
import getRefCornersFromEdge from './utils/getRefCornersFromEdge';
import measureLayout from './utils/measureLayout';
import VisualDebugger from './VisualDebugger';
var getChildSpatialClosestToOrigin = function (children) {
    var childrenClosestToOrigin = sortBy(children, function (_a) {
        var layout = _a.layout;
        return Math.abs((layout === null || layout === void 0 ? void 0 : layout.left) ? layout.left : 0) + Math.abs((layout === null || layout === void 0 ? void 0 : layout.top) ? layout.top : 0);
    }).filter(function (children) { return !children.isInactive && !children.node.classList.contains('inactive'); });
    return first(childrenClosestToOrigin);
};
var getChildSequentialClosestToOrigin = function (lastIndex, children) {
    var childrenClosestToOrigin = sortBy(children, function (_a) {
        var focusIndex = _a.focusIndex;
        return lastIndex - Math.abs(focusIndex || 0);
    }).filter(function (children) { return !children.isInactive && !children.node.classList.contains('inactive'); });
    return first(childrenClosestToOrigin);
};
var TvingFocusable = /** @class */ (function () {
    function TvingFocusable(focusRoot) {
        this.focusKeyStack = [];
        this.focusRoot = focusRoot;
        /**
         * Storage for all focusable components
         */
        this.focusableComponents = {};
        /**
         * Storing current focused key
         */
        this.focusKey = '';
        /**
         * This collection contains focus keys of the elements that are having a child focused
         * Might be handy for styling of certain parent components if their child is focused.
         */
        this.parentsHavingFocusedChild = [];
        this.enabled = false;
        this.nativeMode = false;
        this.throttle = 0;
        this.throttleKeypresses = false;
        this.pressedKeys = {};
        /**
         * Flag used to block key events from this service
         * @type {boolean}
         */
        this.paused = false;
        this.originalKeyDownEventListener = function () {
            // initialize
            return true;
        };
        this.keyDownEventListenerThrottled = throttle(this.originalKeyDownEventListener.bind(this), this.throttle, THROTTLE_OPTIONS);
        this.keyDownEventListener = function () {
            return true;
        };
        this.keyUpEventListener = function () {
            // initialize
            return true;
        };
        this.keyMap = DEFAULT_KEY_MAP;
        this.onKeyEvent = this.onKeyEvent.bind(this);
        this.pause = this.pause.bind(this);
        this.resume = this.resume.bind(this);
        this.setFocus = this.setFocus.bind(this);
        this.getComponentByFocusKey = this.getComponentByFocusKey.bind(this);
        this.getCurrentFocus = this.getCurrentFocus.bind(this);
        this.getCurrentComponent = this.getCurrentComponent.bind(this);
        this.updateAllLayouts = this.updateAllLayouts.bind(this);
        this.navigateByDirection = this.navigateByDirection.bind(this);
        this.initTvingFocusable = this.initTvingFocusable.bind(this);
        this.setKeyMap = this.setKeyMap.bind(this);
        this.debug = false;
        this.visualDebugger = null;
        this.logIndex = 0;
    }
    /**
     * Calculates if the sibling node is intersecting enough with the ref node by the secondary coordinate
     */
    TvingFocusable.isAdjacentSlice = function (refCorners, siblingCorners, isVerticalDirection) {
        var refA = refCorners.a, refB = refCorners.b;
        var siblingA = siblingCorners.a, siblingB = siblingCorners.b;
        var coordinate = isVerticalDirection ? 'x' : 'y';
        var refCoordinateA = refA[coordinate];
        var refCoordinateB = refB[coordinate];
        var siblingCoordinateA = siblingA[coordinate];
        var siblingCoordinateB = siblingB[coordinate];
        var thresholdDistance = (refCoordinateB - refCoordinateA) * ADJACENT_SLICE_THRESHOLD;
        var intersectionLength = Math.max(0, Math.min(refCoordinateB, siblingCoordinateB) - Math.max(refCoordinateA, siblingCoordinateA));
        return intersectionLength >= thresholdDistance;
    };
    TvingFocusable.getPrimaryAxisDistance = function (refCorners, siblingCorners, isVerticalDirection) {
        var refA = refCorners.a;
        var siblingA = siblingCorners.a;
        var coordinate = isVerticalDirection ? 'y' : 'x';
        return Math.abs(siblingA[coordinate] - refA[coordinate]);
    };
    TvingFocusable.getSecondaryAxisDistance = function (refCorners, siblingCorners, isVerticalDirection) {
        var refA = refCorners.a, refB = refCorners.b;
        var siblingA = siblingCorners.a, siblingB = siblingCorners.b;
        var coordinate = isVerticalDirection ? 'x' : 'y';
        var refCoordinateA = refA[coordinate];
        var refCoordinateB = refB[coordinate];
        var siblingCoordinateA = siblingA[coordinate];
        var siblingCoordinateB = siblingB[coordinate];
        var distancesToCompare = [];
        distancesToCompare.push(Math.abs(siblingCoordinateA - refCoordinateA));
        distancesToCompare.push(Math.abs(siblingCoordinateA - refCoordinateB));
        distancesToCompare.push(Math.abs(siblingCoordinateB - refCoordinateA));
        distancesToCompare.push(Math.abs(siblingCoordinateB - refCoordinateB));
        return Math.min.apply(Math, __spreadArray([], __read(distancesToCompare), false));
    };
    /**
     * 인접 노드 (현재 노드의 방향 + 꼭지점 기준 범위 내에 들어오는 노드) 만을 기준으로 우선순위 sorting (입력된 방향과 일치하는 직선상에 위치한 노드들만 가지고 우선순위를 판단)
     * looping 시 지정된 범위 내의 노드만을 탐색하기 위해 호출
     * @param siblings
     * @param currentLayout
     * @param direction
     * @param focusKey
     */
    TvingFocusable.prototype.sortAdjacentSiblingsByPriority = function (siblings, currentLayout, direction, focusKey) {
        this.log('sortAdjacentSiblingsByPriority', "".concat(focusKey !== null && focusKey !== void 0 ? focusKey : ''));
        var isVerticalDirection = direction === DIRECTION_DOWN || direction === DIRECTION_UP;
        // 1. 현재 레이아웃의 기준점을 획득
        // const refCorners = TvingFocusable.getRefCornersFromEdge(direction, false, currentLayout);
        var refCorners = getRefCornersFromCenter(direction, currentLayout);
        var convertedSiblings = siblings.filter(function (sibling) {
            // 2. 형제 노드의 기준점 획득
            var siblingCorners = getRefCornersFromEdge(direction, sibling.layout, true);
            return TvingFocusable.isAdjacentSlice(refCorners, siblingCorners, isVerticalDirection);
        });
        return sortBy(convertedSiblings, function (sibling) {
            var siblingCorners = getRefCornersFromEdge(direction, sibling.layout, true);
            return TvingFocusable.getPrimaryAxisDistance(refCorners, siblingCorners, isVerticalDirection);
        });
    };
    /**
     * Inspired by: https://developer.mozilla.org/en-US/docs/Mozilla/Firefox_OS_for_TV/TV_remote_control_navigation#Algorithm_design
     * Ref Corners are the 2 corners of the current component in the direction of navigation
     * They used as a base to measure adjacent slices
     */
    TvingFocusable.prototype.sortSiblingsByPriority = function (siblings, currentLayout, direction, focusKey, parentComponent) {
        var _this = this;
        var isVerticalDirection = direction === DIRECTION_DOWN || direction === DIRECTION_UP;
        // 1. 현재 레이아웃의 기준점을 획득
        // const refCorners = TvingFocusable.getRefCornersFromEdge(direction, false, currentLayout);
        var refCorners = (parentComponent === null || parentComponent === void 0 ? void 0 : parentComponent.overlapChildren)
            ? getRefCornersFromCenter(direction, currentLayout)
            : getRefCornersFromEdge(direction, currentLayout);
        // 2. 획득된 레이아웃을 기준으로 이웃 노드의 거리를 계산해서 우선순위 정렬
        return sortBy(siblings, function (sibling) {
            // 3. 형제 노드의 기준점을 획득
            var siblingCorners = getRefCornersFromEdge(direction, sibling.layout, true);
            // 4. 인접 노드 여부 체크 (대각선 범위 안에 들어오는지)
            var isAdjacentSlice = TvingFocusable.isAdjacentSlice(refCorners, siblingCorners, isVerticalDirection);
            // 5-1. 형제 노드가 현재 노드의 넓이 or 높이 범위 내에 노드라면, 직선거리를 우선으로 체크
            var primaryAxisFunction = isAdjacentSlice
                ? TvingFocusable.getPrimaryAxisDistance.bind(TvingFocusable)
                : TvingFocusable.getSecondaryAxisDistance.bind(TvingFocusable);
            // 5-2. 인접 노드가 아닌 경우 각 꼭지점 간의 최소 거리를 우선으로 체크
            var secondaryAxisFunction = isAdjacentSlice
                ? TvingFocusable.getSecondaryAxisDistance.bind(TvingFocusable)
                : TvingFocusable.getPrimaryAxisDistance.bind(TvingFocusable);
            var primaryAxisDistance = primaryAxisFunction(refCorners, siblingCorners, isVerticalDirection);
            var secondaryAxisDistance = secondaryAxisFunction(refCorners, siblingCorners, isVerticalDirection);
            /**
             * The higher this value is, the less prioritised the candidate is
             */
            var totalDistancePoints = primaryAxisDistance * MAIN_COORDINATE_WEIGHT + secondaryAxisDistance;
            /**
             * + 1 here is in case of distance is zero, but we still want to apply Adjacent priority weight
             */
            var priority = (totalDistancePoints + 1) / (isAdjacentSlice ? ADJACENT_SLICE_WEIGHT : DIAGONAL_SLICE_WEIGHT);
            _this.log('smartNavigate', "distance (primary, secondary, total weighted) for ".concat(sibling.focusKey, " relative to ").concat(focusKey, " is"), primaryAxisDistance, secondaryAxisDistance, totalDistancePoints);
            _this.log('smartNavigate', "priority for ".concat(sibling.focusKey, " relative to ").concat(focusKey, " is"), priority);
            if (_this.visualDebugger) {
                _this.visualDebugger.drawPoint(siblingCorners.a.x, siblingCorners.a.y, 'yellow', 6);
                _this.visualDebugger.drawPoint(siblingCorners.b.x, siblingCorners.b.y, 'yellow', 6);
            }
            return priority;
        });
    };
    TvingFocusable.prototype.initTvingFocusable = function (_a) {
        var _b = _a === void 0 ? {} : _a, _c = _b.debug, debug = _c === void 0 ? false : _c, _d = _b.visualDebug, visualDebug = _d === void 0 ? false : _d, _e = _b.nativeMode, nativeMode = _e === void 0 ? false : _e, _f = _b.throttle, throttleParam = _f === void 0 ? 0 : _f, _g = _b.throttleKeypresses, throttleKeypresses = _g === void 0 ? false : _g;
        if (!this.enabled) {
            this.enabled = true;
            this.nativeMode = nativeMode;
            this.throttleKeypresses = throttleKeypresses;
            this.debug = debug;
            if (!this.nativeMode) {
                if (Number.isInteger(throttleParam) && throttleParam > 0) {
                    this.throttle = throttleParam;
                }
                this.bindEventHandlers();
                if (visualDebug) {
                    this.visualDebugger = new VisualDebugger();
                    this.startDrawLayouts();
                }
            }
        }
    };
    TvingFocusable.prototype.startDrawLayouts = function () {
        var _this = this;
        var draw = function () {
            requestAnimationFrame(function () {
                if (_this.visualDebugger) {
                    _this.visualDebugger.clearLayouts();
                }
                // forOwn(this.focusableComponents, (component, focusKey) => {
                //   if (this.visualDebugger && component.layout) {
                //     this.visualDebugger.drawLayout(component.layout, focusKey, component.parentFocusKey);
                //   }
                // });
                draw();
            });
        };
        draw();
    };
    TvingFocusable.prototype.destroyTvingFocusable = function () {
        if (this.enabled) {
            this.enabled = false;
            this.nativeMode = false;
            this.throttle = 0;
            this.throttleKeypresses = false;
            this.focusKey = '';
            this.parentsHavingFocusedChild = [];
            this.focusableComponents = {};
            this.paused = false;
            this.keyMap = DEFAULT_KEY_MAP;
            this.unbindEventHandlers();
        }
    };
    TvingFocusable.prototype.getEventType = function (keyCode) {
        return findKey(this.getKeyMap(), function (code) { return keyCode === code; });
    };
    TvingFocusable.prototype.bindEventHandlers = function () {
        var _this = this;
        // We check both because the React Native remote debugger implements window, but not window.addEventListener.
        this.originalKeyDownEventListener = function (event) {
            if (!Object.values(_this.getKeyMap()).some(function (keyCode) { return event.keyCode === keyCode; })) {
                return true;
            }
            // console.groupCollapsed('current focus root', this.focusRoot);
            // console.trace(); // hidden in collapsed group
            // console.groupEnd();
            if (_this.paused) {
                return false;
            }
            if (_this.debug) {
                _this.logIndex += 1;
            }
            var eventType = _this.getEventType(event.keyCode);
            if (!eventType) {
                return false;
            }
            _this.pressedKeys[eventType] = _this.pressedKeys[eventType] ? _this.pressedKeys[eventType] + 1 : 1;
            event.preventDefault();
            event.stopPropagation();
            var keysDetails = {
                pressedKeys: _this.pressedKeys,
            };
            if (eventType === KEY_ENTER && _this.focusKey) {
                _this.onEnterPress(keysDetails);
                return false;
            }
            if ((eventType === KEY_BACK || eventType === KEY_BACK_ANDROID_TV || eventType === KEY_BACK_TIZEN_TV || eventType === KEY_BACK_WEBOS_TV) &&
                _this.focusKey) {
                var component = _this.focusableComponents[_this.focusKey];
                _this.onBackPress(component, keysDetails);
                return false;
            }
            var preventDefaultNavigation = _this.onArrowPress(eventType, keysDetails) === false;
            if (preventDefaultNavigation) {
                _this.log('originalKeyDownEventListener', 'default navigation prevented');
                if (_this.visualDebugger) {
                    _this.visualDebugger.clear();
                }
            }
            else {
                _this.onKeyEvent(event);
            }
            return false;
        };
        this.keyDownEventListener = this.originalKeyDownEventListener;
        // Apply throttle only if the option we got is not undefined to avoid limiting the listener to every animation frame
        if (this.throttle !== undefined) {
            this.keyDownEventListenerThrottled = throttle(this.originalKeyDownEventListener.bind(this), this.throttle, THROTTLE_OPTIONS);
            this.keyDownEventListener = this.keyDownEventListenerThrottled;
        }
        // When throttling then make sure to only throttle key down and cancel any queued functions in case of key up
        this.keyUpEventListener = function (event) {
            if (!Object.values(_this.getKeyMap()).some(function (keyCode) { return event.keyCode === keyCode; })) {
                return true;
            }
            var eventType = _this.getEventType(event.keyCode);
            if (eventType) {
                Reflect.deleteProperty(_this.pressedKeys, eventType);
            }
            if (_this.throttle && !_this.throttleKeypresses) {
                if (eventType !== KEY_BACK &&
                    eventType !== KEY_BACK_ANDROID_TV &&
                    eventType !== KEY_BACK_TIZEN_TV &&
                    eventType !== KEY_BACK_WEBOS_TV &&
                    _this.keyDownEventListenerThrottled) {
                    _this.keyDownEventListenerThrottled.cancel();
                }
            }
            if (eventType === KEY_ENTER && _this.focusKey) {
                _this.onEnterRelease();
            }
            event.stopPropagation();
            event.preventDefault();
            return false;
        };
    };
    TvingFocusable.prototype.unbindEventHandlers = function () {
        // We check both because the React Native remote debugger implements window, but not window.removeEventListener.
        this.originalKeyDownEventListener = function () {
            // initialize
            return false;
        };
        if (this.throttle) {
            this.keyUpEventListener = function () {
                // initialize
                return false;
            };
        }
    };
    TvingFocusable.prototype.onEnterPress = function (keysDetails) {
        var component = this.focusableComponents[this.focusKey];
        /* Guard against last-focused component being unmounted at time of onEnterPress (e.g. due to UI fading out) */
        if (!component) {
            this.log('onEnterPress', 'noComponent');
            return;
        }
        /* Suppress onEnterPress if the last-focused item happens to lose its 'focused' status. */
        if (!component.focusable) {
            this.log('onEnterPress', 'componentNotFocusable');
            return;
        }
        if (component.onEnterPress) {
            component.onEnterPress(keysDetails);
        }
    };
    TvingFocusable.prototype.onEnterRelease = function () {
        var component = this.focusableComponents[this.focusKey];
        /* Guard against last-focused component being unmounted at time of onEnterRelease (e.g. due to UI fading out) */
        if (!component) {
            this.log('onEnterRelease', 'noComponent');
            return;
        }
        /* Suppress onEnterRelease if the last-focused item happens to lose its 'focused' status. */
        if (!component.focusable) {
            this.log('onEnterRelease', 'componentNotFocusable');
            return;
        }
        if (component.onEnterRelease) {
            component.onEnterRelease();
        }
    };
    TvingFocusable.prototype.onArrowPress = function (direction, keysDetails) {
        var component = this.focusableComponents[this.focusKey];
        /* Guard against last-focused component being unmounted at time of onArrowPress (e.g. due to UI fading out) */
        if (!component) {
            this.log('onArrowPress', 'noComponent');
            while (this.focusKeyStack.length > 0) {
                var lastFocusKey = this.focusKeyStack.pop();
                if (lastFocusKey && this.focusableComponents[lastFocusKey] && this.focusableComponents[lastFocusKey].focusable) {
                    component = this.focusableComponents[lastFocusKey];
                    break;
                }
            }
            if (component) {
                // this.setFocus(component.focusKey);
                // this.navigateByDirection(direction, {});
            }
            return undefined;
        }
        /* It's okay to navigate AWAY from an item that has lost its 'focused' status, so we don't inspect
         * component.focusable. */
        return component && component.onArrowPress && component.onArrowPress(direction, keysDetails);
    };
    TvingFocusable.prototype.onBackPress = function (component, keysDetails) {
        /* Guard against last-focused component being unmounted at time of onBackPress (e.g. due to UI fading out) */
        if (!component) {
            this.log('onBackPress', 'noComponent');
            return;
        }
        if (component.onBackPress) {
            component.onBackPress(keysDetails);
        }
        else {
            // If current compopnent has not onBackPress method, then pass to parentComponent.
            var parentComponent = this.focusableComponents[component.parentFocusKey];
            if (parentComponent) {
                // call recursive
                this.onBackPress(parentComponent, keysDetails);
            }
        }
    };
    /**
     * Move focus by direction, if you can't use buttons or focusing by key.
     *
     * @example
     * navigateByDirection('right') // The focus is moved to right
     */
    TvingFocusable.prototype.navigateByDirection = function (direction, focusDetails) {
        if (focusDetails === void 0) { focusDetails = {}; }
        if (this.paused || this.nativeMode) {
            return;
        }
        var validDirections = [DIRECTION_DOWN, DIRECTION_UP, DIRECTION_LEFT, DIRECTION_RIGHT];
        if (validDirections.includes(direction)) {
            this.log('navigateByDirection', 'direction', direction);
            this.smartNavigate(direction, '', focusDetails);
        }
        else {
            this.log('navigateByDirection', "Invalid direction. You passed: `".concat(direction, "`, but you can use only these: "), validDirections);
        }
    };
    TvingFocusable.prototype.onKeyEvent = function (event) {
        if (this.visualDebugger) {
            this.visualDebugger.clear();
        }
        var direction = findKey(this.getKeyMap(), function (code) { return event.keyCode === code; });
        this.smartNavigate(direction || '', '', { event: event });
        event.stopPropagation();
        event.preventDefault();
    };
    /**
     * This function navigates between siblings OR goes up by the Tree
     * Based on the Direction
     */
    TvingFocusable.prototype.smartNavigate = function (direction, fromParentFocusKey, focusDetails, isLoop) {
        var _this = this;
        if (this.nativeMode) {
            return;
        }
        this.log('smartNavigate', 'direction', direction);
        this.log('smartNavigate', 'fromParentFocusKey', fromParentFocusKey);
        this.log('smartNavigate', 'this.focusKey', this.focusKey);
        if (!fromParentFocusKey) {
            forOwn(this.focusableComponents, function (component) {
                // eslint-disable-next-line no-param-reassign
                component.layoutUpdated = false;
            });
        }
        var currentComponent = this.focusableComponents[fromParentFocusKey || this.focusKey];
        this.log('smartNavigate', 'currentComponent', currentComponent ? currentComponent.focusKey : undefined, currentComponent ? currentComponent.node : undefined);
        if (currentComponent) {
            this.updateLayout(currentComponent.focusKey);
            var parentFocusKey_1 = currentComponent.parentFocusKey, focusKey = currentComponent.focusKey, layout = currentComponent.layout;
            var parentComponent_1 = this.focusableComponents[parentFocusKey_1];
            // 1. 방향 체크
            var isVerticalDirection_1 = direction === DIRECTION_DOWN || direction === DIRECTION_UP;
            var isIncrementalDirection_1 = direction === DIRECTION_DOWN || direction === DIRECTION_RIGHT;
            var nextComponent = void 0;
            if (currentComponent.navigationType === NavigationType.SPATIAL) {
                // 2. 현재 노드의 기준점 체크
                var currentCutoffCoordinate_1 = (parentComponent_1 === null || parentComponent_1 === void 0 ? void 0 : parentComponent_1.overlapChildren)
                    ? getCutoffCoordinateFromCenter(isVerticalDirection_1, layout)
                    : getCutoffCoordinateFromEdge(isVerticalDirection_1, layout, isIncrementalDirection_1);
                /**
                 * Get only the siblings with the coords on the way of our moving direction
                 */
                // 3. 방향 & 기준 점 기준 이웃 노드 체크
                var siblings = filter(this.focusableComponents, function (component) {
                    if (component.parentFocusKey === parentFocusKey_1 && component.focusable && component.layout) {
                        _this.updateLayout(component.focusKey);
                        var siblingCutoffCoordinate = (parentComponent_1 === null || parentComponent_1 === void 0 ? void 0 : parentComponent_1.overlapChildren)
                            ? getCutoffCoordinateFromCenter(isVerticalDirection_1, component.layout)
                            : getCutoffCoordinateFromEdge(isVerticalDirection_1, component.layout, isIncrementalDirection_1, true);
                        if (siblingCutoffCoordinate && currentCutoffCoordinate_1) {
                            if (parentComponent_1 === null || parentComponent_1 === void 0 ? void 0 : parentComponent_1.overlapChildren) {
                                return isIncrementalDirection_1 ? siblingCutoffCoordinate > currentCutoffCoordinate_1 : siblingCutoffCoordinate < currentCutoffCoordinate_1;
                            }
                            return isIncrementalDirection_1 ? siblingCutoffCoordinate >= currentCutoffCoordinate_1 : siblingCutoffCoordinate <= currentCutoffCoordinate_1;
                        }
                    }
                    return false;
                });
                if (this.debug) {
                    this.log('smartNavigate', 'currentCutoffCoordinate', currentCutoffCoordinate_1);
                    this.log('smartNavigate', 'siblings', "".concat(siblings.length, " elements:"), siblings.map(function (sibling) { return sibling.focusKey; }).join(', '), siblings.map(function (sibling) { return sibling.node; }));
                }
                if (this.visualDebugger) {
                    var refCorners = getRefCornersFromEdge(direction, layout);
                    this.visualDebugger.drawPoint(refCorners.a.x, refCorners.a.y);
                    this.visualDebugger.drawPoint(refCorners.b.x, refCorners.b.y);
                }
                // 4. 이웃 우선순위 정렬
                // const sortedSiblings = this.sortSiblingsByPriority(siblings, layout, direction, focusKey, parentComponent);
                var sortedSiblings = parentComponent_1 &&
                    parentComponent_1.childrenEndPointAction &&
                    parentComponent_1.childrenEndPointAction !== CHILDREN_ENDPOINT_ACTION.NONE &&
                    parentComponent_1.childrenEndPointAction !== CHILDREN_ENDPOINT_ACTION.ISOLATED
                    ? this.sortAdjacentSiblingsByPriority(siblings, layout, direction, focusKey)
                    : this.sortSiblingsByPriority(siblings, layout, direction, focusKey, parentComponent_1);
                if (isLoop) {
                    sortedSiblings.reverse();
                }
                nextComponent = first(sortedSiblings);
            }
            else if ((currentComponent.navigationType === NavigationType.VERTICAL && isVerticalDirection_1) ||
                (currentComponent.navigationType === NavigationType.HORIZONTAL && !isVerticalDirection_1)) {
                var focusableComponents = Object.values(this.focusableComponents);
                var siblings = focusableComponents
                    .filter(function (component) {
                    return (component.focusable &&
                        component.focusIndex !== undefined &&
                        currentComponent.focusIndex !== undefined &&
                        component.parentFocusKey === parentFocusKey_1 &&
                        (isIncrementalDirection_1 ? component.focusIndex > currentComponent.focusIndex : component.focusIndex < currentComponent.focusIndex));
                })
                    .sort(function (a, b) {
                    if (a.focusIndex === undefined || b.focusIndex === undefined) {
                        return 0;
                    }
                    return a.focusIndex - b.focusIndex;
                });
                if (isLoop) {
                    nextComponent = isIncrementalDirection_1 ? first(focusableComponents) : last(focusableComponents);
                }
                else {
                    nextComponent = isIncrementalDirection_1 ? first(siblings) : last(siblings);
                }
            }
            this.log('smartNavigate', 'nextComponent', nextComponent ? nextComponent.focusKey : undefined, nextComponent ? nextComponent.node : undefined);
            if (nextComponent) {
                this.setFocus(nextComponent.focusKey, focusDetails);
            }
            else {
                // Check setting of childrenEndpointAction
                var EndPointActionResult = checkChildrenEndpointAction(direction, parentComponent_1 === null || parentComponent_1 === void 0 ? void 0 : parentComponent_1.childrenEndPointAction);
                if (EndPointActionResult === CHILDREN_ENDPOINT_ACTION_RESULT.LOOP && !isLoop) {
                    // call recursive when you set the kind of mode 'LOOP'
                    this.smartNavigate(getOppositeDirection(direction), '', focusDetails, true);
                }
                else if (EndPointActionResult === CHILDREN_ENDPOINT_ACTION_RESULT.NONE) {
                    // find next group focusable components
                    this.saveLastFocusedChildKey(parentComponent_1, focusKey);
                    if (parentComponent_1 && parentComponent_1.isFocusBoundary) {
                        var keysDetails = {
                            pressedKeys: this.pressedKeys,
                        };
                        if (parentComponent_1.onChildrenEndPointEvent) {
                            // Check onChildrenEndpoint Event. If it is, you can get the callBackEvent you registered.
                            parentComponent_1.onChildrenEndPointEvent(direction, keysDetails);
                        }
                    }
                    else {
                        this.smartNavigate(direction, parentFocusKey_1, focusDetails);
                    }
                }
            }
        }
    };
    TvingFocusable.prototype.saveLastFocusedChildKey = function (component, focusKey) {
        if (component) {
            this.log('saveLastFocusedChildKey', "".concat(component.focusKey, " lastFocusedChildKey set"), focusKey);
            // eslint-disable-next-line no-param-reassign
            component.lastFocusedChildKey = focusKey;
        }
    };
    TvingFocusable.prototype.log = function (functionName, debugString) {
        var rest = [];
        for (var _i = 2; _i < arguments.length; _i++) {
            rest[_i - 2] = arguments[_i];
        }
        if (this.debug) {
            // eslint-disable-next-line no-console
            console.log.apply(console, __spreadArray(["%c".concat(functionName, "%c").concat(debugString), "background: ".concat(DEBUG_FN_COLORS[this.logIndex % DEBUG_FN_COLORS.length], "; color: black; padding: 1px 5px;"), 'background: #333; color: #BADA55; padding: 1px 5px;'], __read(rest), false));
        }
    };
    /**
     * This function tries to determine the next component to Focus
     * It's either the target node OR the one down by the Tree if node has children components
     * Based on "targetFocusKey" which means the "intended component to focus"
     */
    TvingFocusable.prototype.getNextFocusKey = function (targetFocusKey) {
        var _this = this;
        var targetComponent = this.focusableComponents[targetFocusKey];
        /**
         * Security check, if component doesn't exist, stay on the same focusKey
         */
        if (!targetComponent || this.nativeMode) {
            return targetFocusKey;
        }
        var children = filter(this.focusableComponents, function (component) { return component.parentFocusKey === targetFocusKey && component.focusable && component.node && document.body.contains(component.node); });
        if (children.length > 0) {
            var lastFocusedChildKey = targetComponent.lastFocusedChildKey, preferredChildFocusKey = targetComponent.preferredChildFocusKey;
            this.log('getNextFocusKey', 'lastFocusedChildKey is', lastFocusedChildKey);
            this.log('getNextFocusKey', 'preferredChildFocusKey is', preferredChildFocusKey);
            /**
             * First of all trying to focus last focused child
             */
            if (lastFocusedChildKey && targetComponent.saveLastFocusedChild && this.isParticipatingFocusableComponent(lastFocusedChildKey)) {
                this.log('getNextFocusKey', 'lastFocusedChildKey will be focused', lastFocusedChildKey);
                return this.getNextFocusKey(lastFocusedChildKey);
            }
            /**
             * If there is no lastFocusedChild, trying to focus the preferred focused key
             */
            if (preferredChildFocusKey && targetFocusKey !== preferredChildFocusKey && this.isParticipatingFocusableComponent(preferredChildFocusKey)) {
                this.log('getNextFocusKey', 'preferredChildFocusKey will be focused', preferredChildFocusKey);
                return this.getNextFocusKey(preferredChildFocusKey);
            }
            /**
             * Otherwise, trying to focus something by coordinates
             */
            children.forEach(function (component) { return _this.updateLayout(component.focusKey); });
            var child = void 0;
            if (targetComponent.navigationType === NavigationType.SPATIAL) {
                child = getChildSpatialClosestToOrigin(children);
            }
            else if (lastFocusedChildKey) {
                var lastFocusedChild = this.focusableComponents[lastFocusedChildKey];
                child = getChildSequentialClosestToOrigin(lastFocusedChild.focusIndex || 0, children);
            }
            else {
                // eslint-disable-next-line prefer-destructuring
                child = children[0];
            }
            this.log('getNextFocusKey', 'childKey will be focused', child === null || child === void 0 ? void 0 : child.focusKey);
            return this.getNextFocusKey((child === null || child === void 0 ? void 0 : child.focusKey) || '');
        }
        /**
         * If no children, just return targetFocusKey back
         */
        this.log('getNextFocusKey', 'targetFocusKey', targetFocusKey);
        return targetFocusKey;
    };
    TvingFocusable.prototype.addFocusable = function (_a) {
        var focusKey = _a.focusKey, navigationType = _a.navigationType, focusIndex = _a.focusIndex, node = _a.node, parentFocusKey = _a.parentFocusKey, onEnterPress = _a.onEnterPress, onEnterRelease = _a.onEnterRelease, onArrowPress = _a.onArrowPress, onBackPress = _a.onBackPress, onFocus = _a.onFocus, onBlur = _a.onBlur, saveLastFocusedChild = _a.saveLastFocusedChild, trackChildren = _a.trackChildren, onUpdateFocus = _a.onUpdateFocus, onUpdateHasFocusedChild = _a.onUpdateHasFocusedChild, preferredChildFocusKey = _a.preferredChildFocusKey, autoRestoreFocus = _a.autoRestoreFocus, focusable = _a.focusable, isInactive = _a.isInactive, isFocusBoundary = _a.isFocusBoundary, onChildrenEndPointEvent = _a.onChildrenEndPointEvent, childrenEndPointAction = _a.childrenEndPointAction, overlapChildren = _a.overlapChildren;
        this.focusableComponents[focusKey] = {
            focusKey: focusKey,
            navigationType: navigationType,
            focusIndex: focusIndex,
            node: node,
            parentFocusKey: parentFocusKey,
            onEnterPress: onEnterPress,
            onEnterRelease: onEnterRelease,
            onArrowPress: onArrowPress,
            onBackPress: onBackPress,
            onFocus: onFocus,
            onBlur: onBlur,
            onUpdateFocus: onUpdateFocus,
            onUpdateHasFocusedChild: onUpdateHasFocusedChild,
            saveLastFocusedChild: saveLastFocusedChild,
            trackChildren: trackChildren,
            preferredChildFocusKey: preferredChildFocusKey,
            focusable: focusable,
            isInactive: isInactive,
            isFocusBoundary: isFocusBoundary,
            autoRestoreFocus: autoRestoreFocus,
            onChildrenEndPointEvent: onChildrenEndPointEvent,
            childrenEndPointAction: childrenEndPointAction,
            lastFocusedChildKey: undefined,
            layout: {
                x: 0,
                y: 0,
                width: 0,
                height: 0,
                left: 0,
                top: 0,
                /**
                 * Node ref is also duplicated in layout to be reported in onFocus callback
                 */
                node: node,
            },
            layoutUpdated: false,
            overlapChildren: overlapChildren,
        };
        if (this.nativeMode) {
            return;
        }
        this.updateLayout(focusKey);
        /**
         * If for some reason this component was already focused before it was added, call the update
         */
        if (focusKey === this.focusKey) {
            this.setFocus(focusKey);
        }
    };
    TvingFocusable.prototype.removeFocusable = function (_a) {
        var focusKey = _a.focusKey;
        var componentToRemove = this.focusableComponents[focusKey];
        if (componentToRemove) {
            var parentFocusKey = componentToRemove.parentFocusKey;
            Reflect.deleteProperty(this.focusableComponents, focusKey);
            var parentComponent = this.focusableComponents[parentFocusKey];
            var isFocused = focusKey === this.focusKey;
            /**
             * If the component was stored as lastFocusedChild, clear lastFocusedChildKey from parent
             */
            if (parentComponent && parentComponent.lastFocusedChildKey === focusKey) {
                parentComponent.lastFocusedChildKey = undefined;
            }
            if (this.nativeMode) {
                return;
            }
            /**
             * If the component was also focused at this time, focus another one
             */
            if (isFocused && parentComponent && parentComponent.autoRestoreFocus) {
                this.setFocus(parentFocusKey);
            }
        }
    };
    TvingFocusable.prototype.getNodeLayoutByFocusKey = function (focusKey) {
        var component = this.focusableComponents[focusKey];
        if (component) {
            this.updateLayout(component.focusKey);
            return component.layout;
        }
        return null;
    };
    TvingFocusable.prototype.setCurrentFocusedKey = function (newFocusKey, focusDetails) {
        if (this.isFocusableComponent(this.focusKey) && newFocusKey !== this.focusKey) {
            var oldComponent = this.focusableComponents[this.focusKey];
            var parentComponent = this.focusableComponents[oldComponent.parentFocusKey];
            this.saveLastFocusedChildKey(parentComponent, this.focusKey);
            oldComponent.onUpdateFocus(false);
            var nodeByFocusKey = this.getNodeLayoutByFocusKey(this.focusKey);
            if (nodeByFocusKey) {
                oldComponent.onBlur(nodeByFocusKey, focusDetails);
            }
        }
        this.focusKey = newFocusKey;
        if (this.isFocusableComponent(this.focusKey)) {
            var newComponent = this.focusableComponents[this.focusKey];
            newComponent.onUpdateFocus(true);
            var nodeByFocusKey = this.getNodeLayoutByFocusKey(this.focusKey);
            if (nodeByFocusKey) {
                newComponent.onFocus(nodeByFocusKey, focusDetails);
            }
        }
    };
    TvingFocusable.prototype.updateParentsHasFocusedChild = function (focusKey, focusDetails) {
        var _this = this;
        var parents = [];
        var currentComponent = this.focusableComponents[focusKey];
        /**
         * Recursively iterate the tree up and find all the parents' focus keys
         */
        while (currentComponent) {
            var parentFocusKey = currentComponent.parentFocusKey;
            var parentComponent = this.focusableComponents[parentFocusKey];
            if (parentComponent) {
                var currentParentFocusKey = parentComponent.focusKey;
                parents.push(currentParentFocusKey);
            }
            currentComponent = parentComponent;
        }
        var parentsToRemoveFlag = difference(this.parentsHavingFocusedChild, parents);
        var parentsToAddFlag = difference(parents, this.parentsHavingFocusedChild);
        forEach(parentsToRemoveFlag, function (parentFocusKey) {
            var parentComponent = _this.focusableComponents[parentFocusKey];
            if (parentComponent && parentComponent.trackChildren) {
                parentComponent.onUpdateHasFocusedChild(false);
            }
            _this.onIntermediateNodeBecameBlurred(parentFocusKey, focusDetails);
        });
        forEach(parentsToAddFlag, function (parentFocusKey) {
            var parentComponent = _this.focusableComponents[parentFocusKey];
            if (parentComponent && parentComponent.trackChildren) {
                parentComponent.onUpdateHasFocusedChild(true);
            }
            _this.onIntermediateNodeBecameFocused(parentFocusKey, focusDetails);
        });
        this.parentsHavingFocusedChild = parents;
    };
    TvingFocusable.prototype.updateParentsLastFocusedChild = function (focusKey) {
        var currentComponent = this.focusableComponents[focusKey];
        /**
         * Recursively iterate the tree up and update all the parent's lastFocusedChild
         */
        while (currentComponent) {
            var parentFocusKey = currentComponent.parentFocusKey;
            var parentComponent = this.focusableComponents[parentFocusKey];
            if (parentComponent) {
                this.saveLastFocusedChildKey(parentComponent, currentComponent.focusKey);
            }
            currentComponent = parentComponent;
        }
    };
    TvingFocusable.prototype.getKeyMap = function () {
        return this.keyMap;
    };
    TvingFocusable.prototype.setKeyMap = function (keyMap) {
        this.keyMap = __assign(__assign({}, this.getKeyMap()), keyMap);
    };
    TvingFocusable.prototype.isFocusableComponent = function (focusKey) {
        return !!this.focusableComponents[focusKey];
    };
    /**
     * Checks whether the focusableComponent is actually participating in spatial navigation (in other words, is a
     * 'focusable' focusableComponent). Seems less confusing than calling it isFocusableFocusableComponent()
     */
    TvingFocusable.prototype.isParticipatingFocusableComponent = function (focusKey) {
        return (this.isFocusableComponent(focusKey) &&
            this.focusableComponents[focusKey].focusable &&
            document.body.contains(this.focusableComponents[focusKey].node));
    };
    TvingFocusable.prototype.onIntermediateNodeBecameFocused = function (focusKey, focusDetails) {
        if (this.isParticipatingFocusableComponent(focusKey)) {
            var node = this.getNodeLayoutByFocusKey(focusKey);
            if (node) {
                this.focusableComponents[focusKey].onFocus(node, focusDetails);
            }
        }
    };
    TvingFocusable.prototype.onIntermediateNodeBecameBlurred = function (focusKey, focusDetails) {
        if (this.isParticipatingFocusableComponent(focusKey)) {
            var node = this.getNodeLayoutByFocusKey(focusKey);
            if (node) {
                this.focusableComponents[focusKey].onBlur(node, focusDetails);
            }
        }
    };
    TvingFocusable.prototype.pause = function () {
        this.paused = true;
    };
    TvingFocusable.prototype.resume = function () {
        this.paused = false;
    };
    TvingFocusable.prototype.getComponentByFocusKey = function (focusKey) {
        return this.focusableComponents[focusKey];
    };
    TvingFocusable.prototype.setFocus = function (focusKey, focusDetails) {
        if (focusDetails === void 0) { focusDetails = {}; }
        if (!this.enabled) {
            return;
        }
        // console.groupCollapsed('bt! focus', `${this.focusKey}`, 'to', `${focusKey}`);
        // console.trace(); // hidden in collapsed group
        // console.groupEnd();
        this.log('setFocus', 'focusKey', focusKey);
        var lastFocusedKey = this.focusKey;
        this.focusKeyStack.push(lastFocusedKey);
        if (this.focusKeyStack.length > 100) {
            this.focusKeyStack.shift();
        }
        var newFocusKey = this.getNextFocusKey(focusKey);
        this.log('setFocus', 'newFocusKey', newFocusKey);
        this.setCurrentFocusedKey(newFocusKey, focusDetails);
        this.updateParentsHasFocusedChild(newFocusKey, focusDetails);
        this.updateParentsLastFocusedChild(lastFocusedKey);
    };
    TvingFocusable.prototype.getCurrentFocus = function () {
        return this.focusKey;
    };
    TvingFocusable.prototype.getCurrentComponent = function () {
        return this.focusableComponents[this.focusKey];
    };
    TvingFocusable.prototype.updateAllLayouts = function () {
        var _this = this;
        if (this.nativeMode) {
            return;
        }
        forOwn(this.focusableComponents, function (_, focusKey) {
            _this.updateLayout(focusKey);
        });
    };
    TvingFocusable.prototype.updateLayout = function (focusKey) {
        var component = this.focusableComponents[focusKey];
        if (!component || this.nativeMode || component.layoutUpdated) {
            return;
        }
        var node = component.node, navigationType = component.navigationType;
        if (node) {
            if (navigationType === NavigationType.SPATIAL) {
                component.layout = __assign(__assign({}, measureLayout(node)), { node: node });
            }
            else {
                component.layout = { node: node };
            }
        }
    };
    TvingFocusable.prototype.updateFocusable = function (focusKey, _a) {
        var node = _a.node, preferredChildFocusKey = _a.preferredChildFocusKey, focusable = _a.focusable, isFocusBoundary = _a.isFocusBoundary, onEnterPress = _a.onEnterPress, onEnterRelease = _a.onEnterRelease, onArrowPress = _a.onArrowPress, onBackPress = _a.onBackPress, onFocus = _a.onFocus, onBlur = _a.onBlur, onChildrenEndPointEvent = _a.onChildrenEndPointEvent;
        if (this.nativeMode) {
            return;
        }
        var component = this.focusableComponents[focusKey];
        if (component) {
            component.preferredChildFocusKey = preferredChildFocusKey;
            component.focusable = focusable;
            component.isFocusBoundary = isFocusBoundary;
            component.onEnterPress = onEnterPress;
            component.onEnterRelease = onEnterRelease;
            component.onArrowPress = onArrowPress;
            component.onBackPress = onBackPress;
            component.onFocus = onFocus;
            component.onBlur = onBlur;
            component.onChildrenEndPointEvent = onChildrenEndPointEvent;
            if (node) {
                component.node = node;
            }
        }
    };
    TvingFocusable.prototype.isNativeMode = function () {
        return this.nativeMode;
    };
    TvingFocusable.getInstance = function (rootKey) {
        if (!TvingFocusable.INSTANCES[rootKey]) {
            this.INSTANCES[rootKey] = new TvingFocusable(rootKey);
        }
        return this.INSTANCES[rootKey];
    };
    TvingFocusable.INSTANCES = {};
    return TvingFocusable;
}());
/**
 * Export singleton
 */
/** @internal */
// export const TvingFocusable = new TvingFocusable();
export default TvingFocusable;
// eslint-disable-next-line @typescript-eslint/unbound-method
// export const { initTvingFocusable, destroyTvingFocusable, setKeyMap } = TvingFocusable;
